import { ArrowRight, Check } from 'lucide-react';

import { GradientText, Panel, panelClasses } from '@/components/bedrock';
import { useT } from '@/core/I18nProvider';

import { decisionText } from './decision-text';

import type { Comparison } from '@/features/comparisons';
import type { ReactElement } from 'react';

/**
 * "Which one to pick" — the two decision sentences, as a deliberate MISMATCH.
 *
 * ## Why the two cards are not siblings
 *
 * They shipped as a symmetric pair: two identical panels, one tinted a shade
 * that is four points off white. On a page titled "Syncanix vs <rival>" that is
 * not neutrality, it is a page that forgot to have a point of view — the reader
 * scans two grey rectangles, finds no signal about which one the page is
 * arguing for, and reads neither.
 *
 * So the two are now built differently on purpose:
 *
 * - The **rival card** is quiet. Plain panel, muted body ink, no mark. It says
 *   its piece honestly and does not compete for the eye.
 * - The **Syncanix card** is the one with weight: the brand rule across its top
 *   edge (`accent`, which the Panel doc calls "marks a result card"), the tint
 *   as a real ground, a check medallion, the wordmark set in the brand's own
 *   text gradient, and body ink one step darker.
 *
 * This is the same honesty argument the comparison table makes in its own doc
 * comment, applied one section higher: the reader knows whose site they are on,
 * so marking our side is orientation rather than a trick. What is still NOT
 * done is editorialising the CONTENT — the rival's sentence is the catalog's
 * own, unhedged, and the page has already conceded outright on several rivals.
 *
 * ## The order does not flip
 *
 * Rival first, ours second, in both directions of the document flow. The case
 * for the alternative goes first here for the same reason `FullReview` puts
 * `themBetter` before `usBetter` — a comparison that reaches its own product
 * first has told the reader what kind of document it is.
 */
export function DecisionSplit({ data }: { readonly data: Comparison }): ReactElement {
  const { t } = useT();

  return (
    <section aria-labelledby="which-one">
      <h2
        id="which-one"
        className="text-[clamp(1.5rem,2.6vw,2rem)] font-semibold leading-[1.1] tracking-[-0.036em] text-[color:var(--bd-ink)]"
      >
        {t('vs.detail.whichOne')}
      </h2>

      {/* `items-stretch` so the two cards are the same height whichever
          sentence is longer — an accent rule that stops short of its
          neighbour's bottom edge reads as a rendering bug. */}
      <div className="mt-6 grid grid-cols-1 items-stretch gap-4 md:grid-cols-2">
        <div
          className={panelClasses({
            className: 'flex flex-col gap-3 p-6 sm:p-7',
          })}
        >
          <p className="font-mono rtl:font-sans text-[10.5px] uppercase tracking-[0.14em] text-[color:var(--bd-faint)] rtl:normal-case rtl:tracking-normal">
            {t('vs.detail.pickThemLabel')}
          </p>
          <h3 className="text-[1.15rem] font-semibold tracking-[-0.024em] text-[color:var(--bd-ink)]">
            {data.rivalName}
          </h3>
          <p className="text-pretty text-[0.97rem] leading-[1.65] text-[color:var(--bd-muted)]">
            {decisionText(t(data.pickThemKey))}
          </p>
        </div>

        <Panel accent className="flex flex-col gap-3 bg-none bg-[color:var(--bd-tint)] p-6 sm:p-7">
          <p className="font-mono rtl:font-sans text-[10.5px] uppercase tracking-[0.14em] text-[color:var(--bd-link)] rtl:normal-case rtl:tracking-normal">
            {t('vs.detail.pickUsLabel')}
          </p>
          <h3 className="flex items-center gap-2.5 text-[1.15rem] font-semibold tracking-[-0.024em]">
            <span
              aria-hidden="true"
              className="grid size-6 flex-none place-items-center rounded-full bg-[color:var(--bd-link)] text-white"
            >
              <Check className="size-3.5" strokeWidth={3} />
            </span>
            <GradientText>Syncanix</GradientText>
          </h3>
          <p className="text-pretty text-[0.97rem] leading-[1.65] text-[color:var(--bd-body)]">
            {decisionText(t(data.pickUsKey))}
          </p>
          <span
            aria-hidden="true"
            className="mt-auto inline-flex items-center gap-1.5 pt-1 text-[0.85rem] font-medium text-[color:var(--bd-link)]"
          >
            {t('vs.detail.pickUsNudge')}
            <ArrowRight className="size-3.5 rtl:rotate-180" />
          </span>
        </Panel>
      </div>
    </section>
  );
}
